// HowToUseApp.js
import React from "react";
import "./css/HowToUseApp.css"; // Reference your fun, mobile-friendly CSS here
import { useNavigate } from "react-router-dom";

/**
 * A playful React component that shows users the ropes of KicksApp in a fun, casual way.
 *
 * We’ve added placeholders for screenshots and references to the styles you provided for inspiration.
 *
 * Usage:
 * - Import into your router or main navigation.
 * - Use it as a help/guide page for first-time users or anytime you’d like to give them a tour.
 */

const HowToUseApp = () => {
  const navigate = useNavigate();

  return (
    <div className="container howto-container">
      <p> </p>
      <p> </p>
      <h1 className="fun-heading">Welcome to KicksApp!</h1>
      <p>
        Ready to jump into the ultimate footie experience? KicksApp is here to
        help you plan matches, track stats, and show off your coaching wizardry
        in style. Buckle up—this quick guide will have you up and running faster
        than you can say "GOAL!"
      </p>
      {/* Placeholder for a screenshot or illustration */}
      <h2 className="fun-subtitle">1. First Kicks (Getting Started)</h2>
      <ul className="fun-list">
        <li>
          <strong>After Sign Up:</strong> You’ve completed your profile—now it’s
          time to create your team! The <em>Manage Your Team</em> page is where
          you add players, edit your lineup, and set team preferences. You can:
          <p>
            <strong>Key Features:</strong>
          </p>
          <ul className="fun-list">
            <li>Add new players to your team</li>
            <li>Edit player names if you need to fix typos</li>
            <li>Delete players if they leave or retire</li>
            <li>
              Customize your match settings like game length and team colours
            </li>
          </ul>
          <p>
            <strong>You can always return here later.</strong>
          </p>
        </li>
      </ul>
      <h2 className="fun-subtitle">2. Kickoff Prep (Match Setup)</h2>
      <ol className="fun-list">
        <li>
          <strong>Input the Opponent:</strong> Type in the name of your rival or
          friend.
        </li>
        <li>
          <strong>Home or Away:</strong> Flip the switch for home or away.
        </li>
        <li>
          <strong>Extra Info:</strong> Playing in the snow? Fierce rival? Star
          player recovered from injury? Tell us more so we can feed the AI all
          the important bits.
        </li>
        <li>
          <strong>Onward to Formation:</strong> Hit the Continue button to
          select the winning team!
        </li>
      </ol>
      <img
        src="/MatchSetup.png"
        alt="Match setup screenshot"
        className="screenshot"
      />
      <h2 className="fun-subtitle">3. Dressing Room: Getting Ready to Go</h2>
      <p>It’s almost game time! Time to choose the winning team!</p>
      <p>
        Next is where you set up the match details, choose a formation, and
        assign players to positions before heading into the game.
      </p>
      <ul className="fun-list">
        <li>
          <strong>Select Match Type:</strong> Choose between 5-a-side, 6-a-side,
          7-a-side, 9-a-side, or 11-a-side to define the game size.
        </li>
        <li>
          <strong>Set Match Length:</strong> Pick how long each half (or
          quarter) should be. Options range from 10 to 45 minutes.
        </li>
        <li>
          <strong>Choose Number of Parts:</strong> Decide whether the match will
          be played in halves (2) or quarters (4).
        </li>
        <li>
          <strong>Pick a Formation:</strong> Depending on the match type, choose
          a tactical setup like 2-1-2, 3-1-1, 4-4-2, or 3-5-2. Formations update
          based on the match type. Not seeing your formation? Contact us and we
          will add it!
        </li>
        <li>
          <strong>Assign Players:</strong> Click on each position on the pitch
          layout to assign players. Make sure all positions are filled before
          continuing.
        </li>
        <li>
          <strong>Manage Unassigned Players:</strong> Players not selected in
          the starting lineup can be set as Substitutes (Bench) or Not Selected
          (Home). If you don’t select them, we assume they’re not there.
        </li>
        <li>
          <strong>Continue to Match:</strong> Once all positions are assigned
          and everything looks good, hit Continue to proceed to the live match
          page.
        </li>
      </ul>
      <img
        src="/TeamSelection.png"
        alt="Team Selection screenshot"
        className="screenshot"
      />
      <p>Select Players</p>
      <img
        src="/TeamSelectionOpen.png"
        alt="Team Selection Open screenshot"
        className="screenshot"
      />
      <p> Select Subs</p>
      <img
        src="/TeamSelectionSubs.png"
        alt="Team Selection Subs screenshot"
        className="screenshot"
      />
      <p></p>
      <h2 className="fun-subtitle">4. Game Day Central</h2>
      <p>
        It’s game time! Here’s where you prove you are the ultimate manager:
      </p>
      <ul className="fun-list">
        <li>
          <strong>Start Match:</strong> We’ll ask if you’re ready to kick things
          off. Once you start, your game is officially <em>on</em>!
        </li>
        <li>
          <strong>Update Player Stats:</strong> Tap any player’s icon to open
          their stat card. Goals, assists, shots on/off target, saves—log it all
          with a couple of taps! Someone scored? The scoreboard updates
          automatically!
        </li>
        <li>
          <strong>Live Scoreboard:</strong> Random own goal? Use those plus and
          minus icons to keep the final score accurate.
        </li>
        <li>
          <strong>Subs and Formation Tweaks:</strong> Need to sub out a tired
          winger? Want to shuffle your defenders? Again, press a player, and
          choose the SUB option. The rest is really simple!
        </li>
        <li>
          <strong>Voice Notes (If a part of your plan):</strong> This is where
          the AI kicks in! Press the “Record” button to capture short audio
          commentary. Share those moments or talk tactics for highlight videos.
          Combined with your other inputs, this shapes the final report and
          training plan.
        </li>
        <li>
          <strong>Finish vs. Discard:</strong>
          <ul>
            <li>
              <em>Finish Match</em>: Upload all those sweet stats and finalize
              the game. You can add a last-minute note about the team’s MVP or
              how crazy the ending was.
            </li>
            <li>
              <em>Discard Match</em>: Oops, started the wrong match or changed
              your mind? Discarding wipes everything from this session.
            </li>
          </ul>
        </li>
      </ul>
      <p>Start Match</p>
      <img
        src="/StartMatchModal.png"
        alt="Start Match screenshot"
        className="screenshot"
      />
      <p></p>
      <p>Record your voice notes</p>
      <img
        src="/Recording.png"
        alt="Recording screenshot"
        className="screenshot"
      />
      <p></p>
      <p>Add Player Stats</p>
      <img
        src="/PlayersStats.png"
        alt="Players Stats screenshot"
        className="screenshot"
      />
      <p></p> <p>Make A Sub</p>
      <img
        src="/PlayerSub.png"
        alt="Players Sub screenshot"
        className="screenshot"
      />
      <p></p>
      <h2 className="fun-subtitle">5. Tweak Tactics</h2>
      <p>
        Found the perfect formation in the middle of the match? Or realized your
        star striker’s better off in midfield? The Team Management modal is your
        secret sauce. Assign or swap players into positions, manage who’s
        benched, and keep your team’s formation accurate for those key stats.
      </p>
      <h2 className="fun-subtitle">
        6. Next-Level Coaching (Subscription & Games)
      </h2>
      <ul className="fun-list">
        <li>
          <strong>Free Plan:</strong> If you’re on the free plan, you have three
          goes to try us out!
        </li>
        <li>
          <strong>Upgrade for More:</strong> Want more matches, extended
          recordings, or advanced analytics? Upgrade your plan.
        </li>
        <li>
          <strong>Games Left:</strong> Keep an eye on that number. If you’re
          about to run out, consider leveling up your plan.
        </li>
      </ul>
      <h2 className="fun-subtitle">7. Need a Hand? (Troubleshooting)</h2>
      <ul className="fun-list">
        <li>
          <strong>Internet Connection:</strong> Right now, we still need a
          stable connection, but we’re working on updates!
        </li>
        <li>
          <strong>Microphone Issues:</strong> Make sure your browser or phone
          gave permission to record. No mic access, no voice notes.
        </li>
        <li>
          <strong>Stats Not Updating:</strong> Double-check you’re tapping the
          right player. If all else fails, try refreshing or verify your
          internet connection.
        </li>
        <li>
          <strong>Subscription Snags:</strong> If your plan status looks off or
          you can’t start a new game, head to “Manage Subscription.” Still have
          issues? Contact us!
        </li>
      </ul>
      <h2 className="fun-subtitle">
        8. Exploring the Clubhouse (Main Navigation)
      </h2>
      <p>
        Consider the KicksApp menu your clubhouse of possibilities. Here’s the
        quick lowdown:
      </p>
      <ul className="fun-list">
        <li>
          <strong>Home Page:</strong> Check how many monthly matches you have
          left. If you need more, upgrade your plan to keep the perks coming.
        </li>

        <li>
          <strong>Team Stats:</strong> Check who’s scoring goals, who’s shining
          in midfield, and track your team’s performance over time.
        </li>
        <li>
          <strong>Games Managementp:</strong> Missing a game? got the wrong
          stat? Head in here to fix it!
        </li>
      </ul>
      <h2 className="fun-subtitle">9. Keep Kicking!</h2>
      <p>
        That’s all for now! You’re officially ready to lead your team to
        victory— and have fun doing it. Dive into KicksApp, track your epic
        matches, and show the world you’re a coaching pro. Remember, in
        football,
        <em> anything</em> can happen!
      </p>
      <p className="footnote">
        P.S. If you have any suggestions or run into bumps, just reach out to
        our support. Let’s make KicksApp even better together!
      </p>
      <button onClick={() => navigate("/")} className="how-to-use-button">
        Home
      </button>
    </div>
  );
};

export default HowToUseApp;
